"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  getActiveNotificationEvents,
  acknowledgeNotificationEvent,
  type NotificationEvent,
} from "@/lib/api";
import { useVisibilityPolling } from "@/hooks/useVisibilityPolling";

// [ERP-POLLING-LOAD-REDUCTION-SAFE-DEV-20260707-035C]
// 알림 폴링 5초 → 30초(안전정책: active 탭 60초 이내 유지). hidden 탭은 중지,
// visible 복귀 시 즉시 1회 fetch(useVisibilityPolling). 토큰 없으면 폴링 중지.
const POLL_INTERVAL = 30_000; // 30초 (구 5초 — 부하 저감)

const SOURCE_LABELS: Record<string, string> = {
  warehouse_401402: "창고 401/402호",
  warehouse: "창고",
  store: "매장",
};

function playAlertSound() {
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const now = ctx.currentTime;

    // 두 번 울리는 딩동 소리
    const tones = [
      { freq: 880, start: 0,    dur: 0.18 },
      { freq: 659, start: 0.22, dur: 0.22 },
    ];

    tones.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + start);
      gain.gain.setValueAtTime(0.5, now + start);
      gain.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    });
  } catch {
    // 소리 재생 실패해도 팝업은 유지
  }
}

export default function NotificationPoller() {
  const [pendingEvents, setPendingEvents] = useState<NotificationEvent[]>([]);
  const [activePopup, setActivePopup] = useState<NotificationEvent | null>(null);
  const [acking, setAcking] = useState(false);
  const seenIds = useRef<Set<number>>(new Set());
  const soundEnabled = useRef(false);

  const poll = useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;
      const events = await getActiveNotificationEvents();

      // 새 이벤트 감지
      const newEvents = events.filter((e) => !seenIds.current.has(e.id));
      newEvents.forEach((e) => seenIds.current.add(e.id));

      setPendingEvents(events);

      if (newEvents.length > 0) {
        // 가장 최신 이벤트를 팝업으로 표시
        setActivePopup(newEvents[newEvents.length - 1]);
        if (soundEnabled.current) {
          playAlertSound();
        }
      }
    } catch {
      // 조용히 무시
    }
  }, []);

  useEffect(() => {
    // 첫 사용자 상호작용 후 소리 활성화
    const enableSound = () => { soundEnabled.current = true; };
    window.addEventListener("click", enableSound, { once: true });
    window.addEventListener("keydown", enableSound, { once: true });
    return () => {
      window.removeEventListener("click", enableSound);
      window.removeEventListener("keydown", enableSound);
    };
  }, []);

  // [ERP-POLLING-LOAD-REDUCTION-SAFE-DEV-20260707-035C]
  // 알림 폴링 — visible 30초, hidden 중지, visible 복귀 즉시 1회, 토큰없음 중지.
  useVisibilityPolling(poll, { activeMs: POLL_INTERVAL });

  async function handleAcknowledge(eventId: number) {
    setAcking(true);
    try {
      const userName =
        typeof window !== "undefined"
          ? localStorage.getItem("user_name") || undefined
          : undefined;
      await acknowledgeNotificationEvent(eventId, userName);
      seenIds.current.delete(eventId);
      setPendingEvents((prev) => prev.filter((e) => e.id !== eventId));
      setActivePopup((prev) => (prev?.id === eventId ? null : prev));
    } finally {
      setAcking(false);
    }
  }

  function formatTime(iso: string) {
    return new Date(iso).toLocaleTimeString("ko-KR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  if (!activePopup) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none">
      {/* 배경 오버레이 */}
      <div className="absolute inset-0 bg-black/40 pointer-events-auto animate-in fade-in duration-200" />

      {/* 팝업 카드 */}
      <div className="relative pointer-events-auto w-full max-w-sm mx-4 bg-white rounded-3xl shadow-2xl border-4 border-orange-400 overflow-hidden animate-in zoom-in-95 duration-200">
        {/* 상단 컬러 헤더 */}
        <div className="bg-orange-500 px-6 py-5 flex items-center gap-4">
          <div className="text-5xl animate-bounce">🔔</div>
          <div>
            <div className="text-white font-black text-xl leading-tight">{activePopup.title}</div>
            <div className="text-orange-100 text-sm mt-0.5">
              {formatTime(activePopup.created_at)}
            </div>
          </div>
        </div>

        {/* 본문 */}
        <div className="px-6 py-5">
          {activePopup.message && (
            <p className="text-gray-800 text-base font-medium mb-3">{activePopup.message}</p>
          )}
          {activePopup.source_location && (
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-xl px-4 py-2.5">
              <span>📍</span>
              <span>{SOURCE_LABELS[activePopup.source_location] || activePopup.source_location}</span>
            </div>
          )}

          {/* 대기 중인 추가 이벤트 */}
          {pendingEvents.length > 1 && (
            <div className="mt-3 text-xs text-orange-600 font-semibold bg-orange-50 rounded-lg px-3 py-2">
              + {pendingEvents.length - 1}건의 미확인 알림이 더 있습니다
            </div>
          )}
        </div>

        {/* 확인 버튼 */}
        <div className="px-6 pb-6">
          <button
            onClick={() => handleAcknowledge(activePopup.id)}
            disabled={acking}
            className="w-full py-4 bg-orange-500 hover:bg-orange-600 text-white rounded-2xl text-lg font-black tracking-wide disabled:opacity-50 active:scale-95 transition-all shadow-lg"
          >
            {acking ? "처리 중..." : "✅ 확인했습니다"}
          </button>
          {pendingEvents.length > 1 && (
            <button
              onClick={async () => {
                for (const e of pendingEvents) {
                  await handleAcknowledge(e.id);
                }
              }}
              disabled={acking}
              className="mt-2 w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-2xl text-sm font-semibold disabled:opacity-50"
            >
              전체 확인 ({pendingEvents.length}건)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
